import React, { useState } from 'react';
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  Bug,
  CheckSquare,
  Clock,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  Layers,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  X,
  BarChart3,
  Tag,
  Calendar,
} from 'lucide-react';
import { useKanbanTeamMetrics, useIssuePatterns } from '../../hooks/useJira';
import TechnologyFilters from '../../components/TechnologyFilters';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const TEAM_NAMES = {
  maintenanceSupport: 'Maintenance & Support',
  devops: 'DevOps',
  marketingCloud: 'Marketing Cloud',
};

const PRIORITY_COLORS = {
  Highest: '#DC2626',
  High: '#F97316',
  Medium: '#EAB308',
  Low: '#22C55E',
  Lowest: '#6B7280',
  None: '#9CA3AF',
};

const STATUS_COLORS = ['#3B82F6', '#22C55E', '#EAB308', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'];

// Sortable header component
function SortableHeader({ label, sortKey, currentSort, onSort, align = 'left' }) {
  const isActive = currentSort.key === sortKey;
  const alignClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
      <button
        onClick={() => onSort(sortKey)}
        className={`flex items-center gap-1 w-full ${alignClass}`}
      >
        <span>{label}</span>
        {isActive ? (
          currentSort.direction === 'asc' ? (
            <ArrowUp className="w-3 h-3 text-amber-600" />
          ) : (
            <ArrowDown className="w-3 h-3 text-amber-600" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 text-gray-400" />
        )}
      </button>
    </th>
  );
}

// Generic sorting function
function sortData(data, sortKey, direction) {
  if (!sortKey || !data) return data;

  return [...data].sort((a, b) => {
    let aVal = a[sortKey];
    let bVal = b[sortKey];

    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      const comparison = aVal.localeCompare(bVal);
      return direction === 'asc' ? comparison : -comparison;
    }

    if (direction === 'asc') {
      return aVal - bVal;
    }
    return bVal - aVal;
  });
}

// Hook for managing sort state
function useTableSort(defaultKey = null, defaultDirection = 'asc') {
  const [sort, setSort] = useState({ key: defaultKey, direction: defaultDirection });

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return [sort, handleSort];
}

function StatCard({ title, value, subtitle, icon: Icon, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-amber-100 text-amber-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    indigo: 'bg-indigo-100 text-indigo-600',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

function getPriorityColor(priority) {
  return PRIORITY_COLORS[priority] || PRIORITY_COLORS.None;
}

function getPriorityBadgeClass(priority) {
  switch (priority) {
    case 'Highest':
      return 'bg-red-100 text-red-800';
    case 'High':
      return 'bg-orange-100 text-orange-800';
    case 'Medium':
      return 'bg-yellow-100 text-yellow-800';
    case 'Low':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

// Helper function to get current month date range
function getCurrentMonthDateRange() {
  const today = new Date();
  const startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const endDate = today.toISOString().split('T')[0];
  return { startDate, endDate };
}

// Issue Patterns Modal Component
function IssuePatternsModal({ isOpen, onClose, teamKey }) {
  const patternOptions = { startDate: '2026-01-01' }; // From Jan 1st
  const { data, isLoading, isError, error } = useIssuePatterns(teamKey, patternOptions, isOpen);

  if (!isOpen) return null;

  const patterns = data?.data;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-indigo-600">
            <div className="flex items-center gap-3">
              <Search className="w-6 h-6 text-white" />
              <div>
                <h3 className="text-lg font-semibold text-white">Common Issue Patterns</h3>
                <p className="text-sm text-purple-100">Analysis from January 1, 2026 to present</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
                <p className="mt-3 text-gray-500">Analyzing issue patterns...</p>
                <p className="text-sm text-gray-400 mt-1">This may take a moment</p>
              </div>
            )}

            {isError && (
              <div className="flex flex-col items-center justify-center py-12 text-red-500">
                <AlertCircle className="w-10 h-10" />
                <p className="mt-3 font-medium">Failed to analyze patterns</p>
                <p className="text-sm text-gray-500">{error?.message}</p>
              </div>
            )}

            {patterns && (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{patterns.totalIssues}</p>
                    <p className="text-sm text-gray-600">Total Issues</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{patterns.patterns?.length || 0}</p>
                    <p className="text-sm text-gray-600">Categories Found</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{patterns.issueTypes?.length || 0}</p>
                    <p className="text-sm text-gray-600">Issue Types</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-orange-600">{patterns.monthlyTrend?.length || 0}</p>
                    <p className="text-sm text-gray-600">Months of Data</p>
                  </div>
                </div>

                {/* AI-Analyzed Issue Categories */}
                {patterns.patterns && patterns.patterns.length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Tag className="w-5 h-5 text-purple-500" />
                      Issue Categories (AI-Analyzed)
                      <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full ml-2">
                        Powered by Claude
                      </span>
                    </h4>
                    <div className="space-y-3">
                      {patterns.patterns.map((pattern, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{pattern.pattern}</span>
                              {pattern.severity && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  pattern.severity === 'high' ? 'bg-red-100 text-red-700' :
                                  pattern.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-green-100 text-green-700'
                                }`}>
                                  {pattern.severity}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                                ~{pattern.count} issues ({pattern.percentage}%)
                              </span>
                            </div>
                          </div>
                          {/* Description */}
                          {pattern.description && (
                            <p className="text-sm text-gray-600 mb-2">{pattern.description}</p>
                          )}
                          {/* Progress bar */}
                          <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                            <div
                              className={`h-2 rounded-full ${
                                pattern.severity === 'high' ? 'bg-red-500' :
                                pattern.severity === 'medium' ? 'bg-yellow-500' :
                                'bg-purple-600'
                              }`}
                              style={{ width: `${Math.min(pattern.percentage * 2, 100)}%` }}
                            />
                          </div>
                          {/* Sample issues */}
                          {pattern.sampleIssues && pattern.sampleIssues.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500 mb-1">Example issues:</p>
                              <div className="space-y-1">
                                {pattern.sampleIssues.slice(0, 3).map((issue) => (
                                  <div key={issue.key} className="flex items-center gap-2 text-sm">
                                    <a
                                      href={`https://sunnyrewards.atlassian.net/browse/${issue.key}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-amber-600 hover:underline font-mono text-xs"
                                    >
                                      {issue.key}
                                    </a>
                                    <span className="text-gray-600 truncate">{issue.summary}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Monthly Trend */}
                {patterns.monthlyTrend && patterns.monthlyTrend.length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      Monthly Issue Volume
                    </h4>
                    <div className="flex items-end gap-2 h-32 bg-gray-50 rounded-lg p-4 border border-gray-200">
                      {patterns.monthlyTrend.map((month) => {
                        const maxCount = Math.max(...patterns.monthlyTrend.map(m => m.count));
                        const height = maxCount > 0 ? (month.count / maxCount) * 100 : 0;
                        return (
                          <div key={month.month} className="flex-1 flex flex-col items-center gap-1">
                            <div
                              className="w-full bg-blue-500 rounded-t"
                              style={{ height: `${height}%`, minHeight: month.count > 0 ? '4px' : '0' }}
                            />
                            <span className="text-xs text-gray-500">{month.month.slice(5)}</span>
                            <span className="text-xs font-medium text-gray-700">{month.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Issue Types */}
                {patterns.issueTypes && patterns.issueTypes.length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-green-500" />
                      Issue Type Distribution
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {patterns.issueTypes.map((type) => (
                        <div key={type.type} className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
                          <p className="text-lg font-bold text-gray-900">{type.count}</p>
                          <p className="text-sm text-gray-600">{type.type}</p>
                          <p className="text-xs text-gray-400">{type.percentage}%</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Priority Distribution */}
                {patterns.priorityDistribution && patterns.priorityDistribution.length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      Priority Distribution
                    </h4>
                    <div className="flex gap-2">
                      {patterns.priorityDistribution.map((p) => (
                        <div
                          key={p.priority}
                          className="flex-1 text-center py-2 px-3 rounded-lg"
                          style={{ backgroundColor: `${PRIORITY_COLORS[p.priority] || '#9CA3AF'}20` }}
                        >
                          <p className="text-lg font-bold" style={{ color: PRIORITY_COLORS[p.priority] || '#9CA3AF' }}>
                            {p.count}
                          </p>
                          <p className="text-xs text-gray-600">{p.priority}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Labels */}
                {patterns.topLabels && patterns.topLabels.length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Tag className="w-5 h-5 text-indigo-500" />
                      Top Labels
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {patterns.topLabels.map((label) => (
                        <span
                          key={label.label}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm"
                        >
                          {label.label}
                          <span className="text-xs bg-indigo-200 text-indigo-800 px-1.5 py-0.5 rounded-full">
                            {label.count}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Insights */}
                {patterns.aiAnalysis?.insights && patterns.aiAnalysis.insights.length > 0 && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      AI Insights
                    </h4>
                    <ul className="space-y-2">
                      {patterns.aiAnalysis.insights.map((insight, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="text-blue-500 mt-1">•</span>
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* AI Recommendations */}
                {patterns.aiAnalysis?.recommendations && patterns.aiAnalysis.recommendations.length > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Recommendations
                    </h4>
                    <ul className="space-y-2">
                      {patterns.aiAnalysis.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="text-green-500 font-bold mt-0.5">{idx + 1}.</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function KanbanTeamPage({ teamKey, onNavigate }) {
  // Initialize with current month date range
  const [filters, setFilters] = useState(() => getCurrentMonthDateRange());
  const [showPatternsModal, setShowPatternsModal] = useState(false);
  const { data, isLoading, isError, error, refetch, isFetching } = useKanbanTeamMetrics(teamKey, filters);

  // Sort state for the open bugs table (default: most days open first)
  const [bugsSort, handleBugsSort] = useTableSort('daysOpen', 'desc');

  // Reset filters to current month when team changes
  React.useEffect(() => {
    setFilters(getCurrentMonthDateRange());
    setShowPatternsModal(false);
  }, [teamKey]);

  const teamName = TEAM_NAMES[teamKey] || teamKey;

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
          <p className="text-gray-500 text-lg">Loading {teamName} Kanban data...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 text-red-500">
          <AlertCircle className="w-12 h-12" />
          <p className="text-lg font-medium">Failed to load Kanban data</p>
          <p className="text-sm text-gray-500">{error?.message}</p>
          <button
            onClick={() => refetch()}
            className="mt-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const metrics = data?.data;
  const {
    statusBreakdown,
    priorityBreakdown,
    weeklyThroughput,
    openBugs,
  } = metrics || {};

  // Prepare pie chart data for status
  const statusPieData = statusBreakdown?.map((s, idx) => ({
    name: s.status,
    value: s.count,
    color: STATUS_COLORS[idx % STATUS_COLORS.length],
  }));

  // Prepare priority data
  const priorityData = priorityBreakdown?.map((p) => ({
    name: p.priority,
    count: p.count,
    fill: getPriorityColor(p.priority),
  }));

  // Sort open bugs
  const sortedBugs = openBugs ? sortData(openBugs, bugsSort.key, bugsSort.direction) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={() => onNavigate('technology')}
            className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Technology Dashboard
          </button>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Layers className="w-7 h-7 text-purple-600" />
            {metrics?.team || teamName}
          </h2>
          <p className="text-gray-500 mt-1">
            Kanban board metrics and throughput analysis
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPatternsModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 shadow-sm"
          >
            <Search className="w-4 h-4" />
            Common Patterns
          </button>
          <a
            href={`https://sunnyrewards.atlassian.net/jira/software/c/projects/${metrics?.projectKey}/boards/${metrics?.boardId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ExternalLink className="w-4 h-4" />
            Open in Jira
          </a>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <TechnologyFilters
        onFilterChange={handleFilterChange}
        showSprintFilter={false}
        initialFilters={filters}
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Total Issues"
          value={metrics?.totalIssues || 0}
          subtitle="On the board"
          icon={Layers}
          color="purple"
        />
        <StatCard
          title="Open Bugs"
          value={metrics?.bugCount || 0}
          subtitle={`${openBugs?.length || 0} still open`}
          icon={Bug}
          color="red"
        />
        <StatCard
          title="Tasks"
          value={metrics?.taskCount || 0}
          subtitle="Active tasks"
          icon={CheckSquare}
          color="blue"
        />
        <StatCard
          title="Avg Turnaround"
          value={`${metrics?.avgTurnaroundDays || 0} days`}
          subtitle="Time to completion"
          icon={Clock}
          color={metrics?.avgTurnaroundDays > 7 ? 'orange' : 'green'}
        />
        <StatCard
          title="Last 30 Days"
          value={`+${metrics?.last30Days?.created || 0} / -${metrics?.last30Days?.resolved || 0}`}
          subtitle="Created / Resolved"
          icon={TrendingUp}
          color="indigo"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Weekly Throughput */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Weekly Throughput (Last 4 Weeks)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyThroughput}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip
                  formatter={(value) => [value, 'Issues Resolved']}
                  labelFormatter={(label, payload) => {
                    if (payload && payload[0]) {
                      return `${payload[0].payload.weekStart} to ${payload[0].payload.weekEnd}`;
                    }
                    return label;
                  }}
                />
                <Bar dataKey="resolved" name="Resolved" fill="#22C55E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Distribution Pie */}
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Layers className="w-5 h-5 text-purple-500" />
            Status Distribution
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {statusPieData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Priority Breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-500" />
          Priority Breakdown
        </h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={priorityData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {priorityData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Open Bugs Table */}
      {openBugs && openBugs.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Bug className="w-5 h-5 text-red-500" />
            Open Bugs ({openBugs.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader label="Key" sortKey="key" currentSort={bugsSort} onSort={handleBugsSort} />
                  <SortableHeader label="Summary" sortKey="summary" currentSort={bugsSort} onSort={handleBugsSort} />
                  <SortableHeader label="Priority" sortKey="priority" currentSort={bugsSort} onSort={handleBugsSort} />
                  <SortableHeader label="Status" sortKey="status" currentSort={bugsSort} onSort={handleBugsSort} />
                  <SortableHeader label="Assignee" sortKey="assignee" currentSort={bugsSort} onSort={handleBugsSort} />
                  <SortableHeader label="Days Open" sortKey="daysOpen" currentSort={bugsSort} onSort={handleBugsSort} align="right" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedBugs.slice(0, 20).map((bug) => (
                  <tr key={bug.key} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <a
                        href={`https://sunnyrewards.atlassian.net/browse/${bug.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-amber-600 hover:underline font-medium"
                      >
                        {bug.key}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-md truncate">{bug.summary}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadgeClass(bug.priority)}`}>
                        {bug.priority || 'None'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{bug.status}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{bug.assignee || 'Unassigned'}</td>
                    <td className="px-4 py-3 text-sm text-right">
                      <span className={`font-medium ${bug.daysOpen > 14 ? 'text-red-600' : bug.daysOpen > 7 ? 'text-orange-600' : 'text-gray-600'}`}>
                        {bug.daysOpen}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {openBugs.length > 20 && (
              <p className="text-sm text-gray-500 mt-3 text-center">
                Showing 20 of {openBugs.length} open bugs
              </p>
            )}
          </div>
        </div>
      )}

      {/* Status Breakdown Table */}
      {statusBreakdown && statusBreakdown.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Layers className="w-5 h-5 text-purple-500" />
            Issues by Status
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {statusBreakdown.map((status, idx) => (
              <div
                key={status.status}
                className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200"
              >
                <p className="text-2xl font-bold" style={{ color: STATUS_COLORS[idx % STATUS_COLORS.length] }}>
                  {status.count}
                </p>
                <p className="text-sm text-gray-600 truncate" title={status.status}>
                  {status.status}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Issue Patterns Modal */}
      <IssuePatternsModal
        isOpen={showPatternsModal}
        onClose={() => setShowPatternsModal(false)}
        teamKey={teamKey}
      />
    </div>
  );
}
